import { useCallback, useState } from "react";
import { SelectedDeliveryOption } from "../../types";

interface UseBuyTimeInputParams {
  initialBuyTimeMinutes?: string | number;
  supplierId: string | null | undefined;
  selection: SelectedDeliveryOption | undefined;
  onSelectionChange: (selection: SelectedDeliveryOption | undefined) => void;
  isExpired: boolean;
}

export function useBuyTimeInput({
  initialBuyTimeMinutes,
  supplierId,
  selection,
  onSelectionChange,
  isExpired,
}: UseBuyTimeInputParams) {
  const [buyTimeValue, setBuyTimeValue] = useState<string>(() => {
    if (initialBuyTimeMinutes) {
      const minutes = parseInt(String(initialBuyTimeMinutes));
      if (!isNaN(minutes) && minutes > 0) {
        const hours = minutes / 60;
        const roundedHours = Math.round(hours * 10) / 10;
        return String(roundedHours);
      }
    }
    return "";
  });

  const isBuyTimeSelected = selection?.customerAction === "buytime";

  const handleBuyTimeToggle = useCallback(() => {
    if (isExpired) return;
    const newAction = isBuyTimeSelected ? "confirm" : "buytime";

    onSelectionChange({
      supplierId,
      customerAction: newAction,
      buyTimeHours: newAction === "buytime" ? buyTimeValue : undefined,
    });
  }, [
    isExpired,
    isBuyTimeSelected,
    onSelectionChange,
    supplierId,
    buyTimeValue,
  ]);

  const handleBuyTimeValueChange = useCallback(
    (val: string) => {
      setBuyTimeValue(val);

      if (!val) {
        onSelectionChange({
          supplierId,
          customerAction: "buytime",
          buyTimeHours: undefined,
        });
        return;
      }

      const hours = parseFloat(val);
      if (isNaN(hours) || hours <= 0) {
        return;
      }

      onSelectionChange({
        supplierId,
        customerAction: "buytime",
        buyTimeHours: val,
      });
    },
    [onSelectionChange, supplierId],
  );

  return {
    buyTimeValue,
    isBuyTimeSelected,
    handleBuyTimeToggle,
    handleBuyTimeValueChange,
  };
}
